import { getOwnPropertyDescriptors, keys } from '@rehaje/shared'
import { Referencer, ScopeManager } from 'eslint-scope'

import { t } from '../ast/utils'

import type { TransformOptions } from '../transform'
import type {
  AnyNode,
  Identifier,
  JSXAttribute,
  JSXClosingElement,
  JSXClosingFragment,
  JSXElement,
  JSXEmptyExpression,
  JSXExpressionContainer,
  JSXFragment,
  JSXIdentifier,
  JSXMemberExpression,
  JSXNamespacedName,
  JSXOpeningElement,
  JSXOpeningFragment,
  JSXSpreadAttribute,
  JSXSpreadChild,
  JSXText
} from 'acorn'
import type { AnalyzeOptions } from 'eslint-scope'

export type { Identifier } from 'acorn'

const whiteList = keys(getOwnPropertyDescriptors(globalThis))

class JSXReferencer extends Referencer {
  declare scopeManager: ScopeManager

  JSXAttribute(node: JSXAttribute) {
    this.visit(node.value)
    this.close(node)
  }

  JSXClosingElement(node: JSXClosingElement) {
    this.visit(node.name)
    this.close(node)
  }

  JSXClosingFragment(node: JSXClosingFragment) {
    this.close(node)
  }

  JSXElement(node: JSXElement) {
    if (node.openingElement) this.visit(node.openingElement)
    if (node.closingElement) this.visit(node.closingElement)
    node.children.forEach((child) => this.visit(child))
    this.close(node)
  }

  JSXEmptyExpression(node: JSXEmptyExpression) {
    this.close(node)
  }

  JSXExpressionContainer(node: JSXExpressionContainer) {
    this.visit(node.expression)
    this.close(node)
  }

  JSXFragment(node: JSXFragment) {
    if (node.openingFragment) this.visit(node.openingFragment)
    if (node.closingFragment) this.visit(node.closingFragment)
    node.children.forEach((child) => this.visit(child))
    this.close(node)
  }

  JSXIdentifier(node: JSXIdentifier) {
    if (/^[A-Z]/.test(node.name)) this.visit(t.identifier(node.name))
    this.close(node)
  }

  JSXMemberExpression(node: JSXMemberExpression) {
    this.visit(node.object)
    this.visit(node.property)
    this.close(node)
  }

  JSXNamespacedName(node: JSXNamespacedName) {
    this.visit(node.namespace)
    this.visit(node.name)
    this.close(node)
  }

  JSXOpeningElement(node: JSXOpeningElement) {
    this.visit(node.name)
    node.attributes.forEach((attr) => this.visit(attr))
    this.close(node)
  }

  JSXOpeningFragment(node: JSXOpeningFragment) {
    this.close(node)
  }

  JSXSpreadAttribute(node: JSXSpreadAttribute) {
    this.visit(node.argument)
    this.close(node)
  }

  JSXSpreadChild(node: JSXSpreadChild) {
    this.visit(node.expression)
    this.close(node)
  }

  JSXText(node: JSXText) {
    this.visit(t.literal(node.value))
    this.close(node)
  }
}

export function extractGlobalVariables(
  node: AnyNode,
  options: TransformOptions = {}
): Identifier[] {
  const analyzeOptions: AnalyzeOptions = options.module
    ? { ecmaVersion: 2022, sourceType: 'module' }
    : { ecmaVersion: 2022, sourceType: 'script' }
  // @ts-ignore
  const manager = new ScopeManager(analyzeOptions)
  const referencer = new JSXReferencer(analyzeOptions, manager)
  referencer.visit(node)
  return referencer.scopeManager.globalScope.through
    .filter((reference) => !whiteList.includes(reference.identifier.name))
    .map((reference) => reference.identifier as Identifier)
}
